import React, { useEffect, useState, useRef } from 'react';
import styles from './VideoEmbed.css';
import { Loading } from '../loading';
import { Helmet } from 'react-helmet';
import { useCurrentBreakPoint, useMounted } from '../commons';
import { adBreakPoint } from '../../lib/helpers';

export const TwitterScript = {
  status: 'initial',
  script: undefined,
  twitIds: [],
};

const embedTwitter = (twitId, element) => {
  if (TwitterScript.status === 'loaded') {
    window.twttr.widgets.createVideo(twitId, element);
  } else {
    TwitterScript.twitIds.push([twitId, element]);
  }
  if (TwitterScript.status === 'initial') {
    TwitterScript.script = document.createElement('script');
    TwitterScript.script.src ='https://platform.twitter.com/widgets.js';
    document.head.appendChild(TwitterScript.script);
    TwitterScript.status = 'loading';
    TwitterScript.script.onload = () => {
      TwitterScript.twitIds.forEach(
        ([twitId, element]) => window.twttr.widgets.createVideo(twitId, element));
      TwitterScript.status = 'loaded';
      TwitterScript.twitIds = [];
      document.head.removeChild(TwitterScript.script);
    };
    TwitterScript.script.onerror = () => {
      const message = document.createElement('h2');
      const twitUrl = 'https://twitter.com/user/status/'+ twitId;
      message.innerHTML = 'Je maakt gebruikt van een plugin die deze embed blokkeert. ' +
        'Klik' + '<a href=\'' + twitUrl + '\' class="brand-link" target="_blank" rel="noopener noreferrer"> hier </a>' 
        + 'om het item te bekijken.';
      element.appendChild(message);
    };

  }
};

class TwitterVideo extends React.Component {

  constructor(props) {
    super(props);
    this.ref = React.createRef();
  }

  componentDidMount() {
    embedTwitter(this.props.twitId, this.ref.current);
  }
  
  componentDidUpdate(prevProps) {
    if (this.props.twitId !== prevProps.twitId ) {
      embedTwitter(this.props.twitId, this.ref.current);
    }
  }
  
  render() {
    return (
      <div ref={this.ref} className={styles.wrapper}>
      </div>
    );
  }
}

const RatioIframe = props =>
  <iframe
    frameBorder="0"
    allowFullScreen={true}
    className={styles.video}
    { ...props }
  />;

const OEmbed = ({ url }) => {
  const [html, setHtml] = useState();
  const [script, setScript] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState();
  const unmounted = useRef(false);
  useEffect(() => () => {
    unmounted.current = true;
  }, []);

  useEffect(() => {
    if(url) {
      setScript(null);
      setHtml(null);
      setLoading(true);
      fetch(url)
        .then(response => response.json())
        .then(({ html }) => {
          if(!unmounted.current) {
            setHtml(html);
            if(html) {
              const match = html.match(/<script[^>]+src=(['"])(.*?)\1/i);
              setScript(match && match[2]);
            }
          }

        })
        .catch(e => !unmounted.current ? setError(e) : null)
        .finally(() => !unmounted.current ? setLoading(false): null);
    }
  }, [url]);
  return <>
    {script && <Helmet>
      <script async src={script} />
    </Helmet>}
    {loading && <Loading/>}
    {error && <pre>{error.toString()}</pre>}
    {html && <div style={{
      margin: 'auto', display: 'flex', justifyContent: 'center',
    }} dangerouslySetInnerHTML={{ __html: html }}/>}
  </>;
};

export const VideoEmbed = ({ uri }) => {
  const [type, id] = uri.split(':');
  const [username, embedId] = id.includes('/') ? id.split('/') : ['', ''];
  const currentBreakPoint = useCurrentBreakPoint();
  const isMounted = useMounted();
  const twitchType = id === 'dumpert' ? `channel=${id}` : `video=v${id}`;

  const fbEmbedSize = {
    'L': { width: '1020', height: '573' },
    'XL': { width: '1175', height: '660' },
  };

  switch (type) {
  case 'twitch':
    if (!isMounted) {
      return <div style={{ 
        height: '100%', 
        width: '100%', 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Loading smallLoader showSuffix={false}/>
      </div>;
    } else {
      return <iframe 
        src={`https://player.twitch.tv/?${twitchType}&parent=${window.location.hostname}`} 
        frameBorder="0" 
        allowFullScreen={true} 
        scrolling="no" 
        height="100%" 
        width="100%" 
      />;
    }
  case 'kijk.nl':
    return (
      <RatioIframe
        src={`https://embed.kijk.nl/?token=mediahuis_dumpert_web&video=${id}`}
        allow="autoplay; encrypted-media; fullscreen"
        scrolling="no"
      />
    );
  case 'youtube':
    return (
      <RatioIframe
        src={`https://www.youtube.com/embed/${id}`}
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
      />
    );
  case 'twitter':
    return <TwitterVideo twitId={id}/>;
  case 'facebook':
    return (
      <>
        {['XS', 'S', 'M'].includes(adBreakPoint(currentBreakPoint)) && (
          <>
            {/* Facebook embed player sizes to content fluid */}
            <Helmet>
              <script async defer src="https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v3.2"></script>
            </Helmet>     
            <div 
              className={`fb-video ${styles.facebook_embed}`} 
              data-href={`https://www.facebook.com/${username}/videos/${embedId}/`}
              data-allowfullscreen="true"
              data-show-text="false"
            ></div>        
          </>
        )}
        {/* Facebook embed takes fixed width and height */}
        {['L', 'XL'].includes(adBreakPoint(currentBreakPoint)) && (
          <iframe 
            src={'https://www.facebook.com/plugins/video.php'
        + '?href=https%3A%2F%2Fwww.facebook.com%2F'
        + username
        + '%2Fvideos%2F'
        + embedId
        + '%2F&show_text=0&width='
        + fbEmbedSize[adBreakPoint(currentBreakPoint)].width
        + '&height='
        + fbEmbedSize[adBreakPoint(currentBreakPoint)].height
            }        
            width={fbEmbedSize[adBreakPoint(currentBreakPoint)].width} 
            height={fbEmbedSize[adBreakPoint(currentBreakPoint)].height}
            style={{ border: 'none', overflow: 'hidden' }} 
            scrolling="no" 
            frameBorder="0" 
            allowFullScreen={true}
          />
        )}
      </>
    );
  case 'tiktok':
    return (
      <OEmbed
        url={`https://www.tiktok.com/oembed?url=https://www.tiktok.com/@${username}/video/${embedId}`}
      />
    );
  case 'instagram':
    // eslint-disable-next-line no-case-declarations
    const params = new URLSearchParams();
    params.append('url', `https://instagr.am/p/${id}/`);
    params.append('access_token', '227370684433724|d64249ee8dcd60ac7ef3e285ad57ccc9');
    params.append('maxwidth', '320');
    params.append('hidecaption', 'true');
    return (<OEmbed
      url={`https://graph.facebook.com/v11.0/instagram_oembed/?${params.toString()}`}
    />
    );
  default:
    break;
  }
  return null;
};
